import { Injectable, EventEmitter } from '@angular/core';
import { FormGroup, FormBuilder, FormArray, ValidatorFn, AbstractControl } from '@angular/forms';
import { Router } from '@angular/router';
import { UserService } from './user.service';
import { TeamService } from './team.service';
import { User } from 'app/models/user';
import { Team } from 'app/models/team';
import { Contact } from 'app/models/contact';
import { ContactService } from './contactService';
import { WorkflowTemplate } from 'app/models/workflow-template';
import { WorkflowFile } from 'app/models/workflow-file';
import { WorkflowStepCompletion } from 'app/models/workflow-step-completion';
import { WorkflowTemplateService } from './workflow-template.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { WorkflowForm } from 'app/models/workflow-form';
import { StepType, WorkflowStep } from 'app/models/workflow-step';
import { formGroupPatchValueNullFriendly } from 'app/helpers/utils';
import { finalize, tap } from 'rxjs/operators';
import { TreeNode } from 'primeng/api/treenode';
import { WorkflowExecVariable } from 'app/models/workflow-exec-variable';
import { TagService } from './tag.service';
import { Tag } from 'app/models/tag';
import * as _ from "lodash";
import { AutoStartFormSettings, WorkflowAutoStart } from 'app/models/workflow-auto-start';
import { WorkflowAutoUpload } from 'app/models/workflow-auto-upload';


@Injectable({ providedIn: 'root' })
export class WorkflowWizardService {

  public get WIZARD_FIRST_STEP() : string {
    return "general-settings";
  }
  public get WIZARD_SECOND_STEP() : string {
    return "materials";
  }
  public get WIZARD_THIRD_STEP() : string {
    return "workflow-rules";
  }
  public get WIZARD_NEW_STEP() : string {
    return this.currentStep && this.currentStep.value.id? `step/${this.currentStep.value.id}/materials`: "new-step";
  }
  public get WIZARD_NEW_STEP_RULES() : string {
    return this.currentStep && this.currentStep.value.id? `step/${this.currentStep.value.id}/auto-actions`: "new-step-rules";
  }
  public get WIZARD_STEPS_TREE() : string {
    return "steps-tree";
  }
  
  get baseURL(): string {
    return `workflows/template/${this.currentId}/`;
  }

  workflowTemplateFormGroup: FormGroup;

  currentId = 'new';
  currentStepURL: string;
  currentStep: FormGroup;

  isLoading: boolean = false;

  public teams: Team[] = [];
  public users: User[] = [];
  public contacts: Contact[] = [];

  workflowType: string = 'submission'; 

  onInit: EventEmitter<any> = new EventEmitter();

  allowedTags: Tag[] = [];

  constructor(private contactService: ContactService,
    private userService: UserService, public teamService: TeamService, private _route: Router,
    private workflowTemplateService: WorkflowTemplateService, public tagService: TagService) {

  }

  // init the general settings form and empty all the workflow variables arrays
  init(workflowTemplate: WorkflowTemplate) {

    this.teamService.getAll(false).subscribe(t => this.teams = t);

    this.userService.getAll().subscribe(u => { this.users = u;});

    this.contactService.getAll().subscribe(c => { this.contacts = c; });

    this.currentStepURL = this.WIZARD_FIRST_STEP;

    this.currentId =  (workflowTemplate.id)? workflowTemplate.id.toString(): 'new';

    this.tagService.currentTagsSubject.subscribe((t) => { 
      this.allowedTags = [];
      
      if (this.currentId == 'new') {
        if (this.userService.getPermissions('super.admin.workflows.create').all) {
          this.allowedTags = t;
        } else if (this.userService.getPermissions('super.admin.workflows.create').tags) {
          this.allowedTags = _.intersectionBy(t, this.userService.getPermissions('super.admin.workflows.create').tags, 'id');
        }
      }
      else {
        if (this.userService.getPermissions('super.admin.workflows.create.update').all) {
          this.allowedTags = t;
        } else if (this.userService.getPermissions('super.admin.workflows.create.update').tags) {
          this.allowedTags = _.intersectionBy(t, this.userService.getPermissions('super.admin.workflows.create.update').tags, 'id');
        }
      }                                 
    });
    
    this.isLoading = false;

    this.workflowType = 'submission';
    
    this.currentStep = null;

    this.checkWorkflowType(workflowTemplate.initial_step_completion);

    this.workflowTemplateFormGroup = this.workflowTemplateService.workflowTemplateFormGroup(workflowTemplate);
    formGroupPatchValueNullFriendly(this.workflowTemplateFormGroup, workflowTemplate);
    this.onInit.emit();
  }

  hasMoreThanNextSteps(step: WorkflowStep) {
    let isTrue = false;
    if(step != null){
      step.step_completions.forEach(s => {
        if(s.next_step != null){
          isTrue = true;
        }
      });
    }
    return isTrue;
  }

  checkWorkflowType(initial_step_completion: WorkflowStepCompletion){
    if (this.currentId != 'new' || (initial_step_completion.next_step != null 
      && (initial_step_completion.next_step.workflow_step_type_id == StepType.AUTO ||initial_step_completion.next_step.workflow_step_type_id == StepType.CUSTOM || this.hasMoreThanNextSteps(initial_step_completion.next_step) ))) {
      this.workflowType = 'custom';
    }
    else {
      if (initial_step_completion.next_step) {
        if (initial_step_completion.next_step.step_completions.length == 1 
            && initial_step_completion.next_step.workflow_step_type_id == StepType.REVIEW) {
          this.workflowType = 'review';
        } else if (initial_step_completion.next_step.step_completions.length == 2 
          && initial_step_completion.next_step.workflow_step_type_id == StepType.APPROVAL) {
          this.workflowType = 'approval';
        } else {
          this.workflowType = 'custom';
        }
      }
      else {
        this.workflowType = 'submission';
      }
    }
  }

  removeFileFromRules(file: WorkflowFile, workflowStepCompletionFormGroup: FormGroup = (this.workflowTemplateFormGroup.controls['initial_step_completion'] as FormGroup)) {
    (workflowStepCompletionFormGroup.controls['auto_uploads'] as FormArray).controls.forEach(a => {
      let autoUploadFormGroup = (a as FormGroup);
      let files: WorkflowFile[] = autoUploadFormGroup.controls['files'].value as WorkflowFile[] || [];
      
      let fileIndex = files.findIndex(i => i.name == file.name);
      if (fileIndex !== -1) {
        files.splice(fileIndex, 1);
        autoUploadFormGroup.controls['files'].updateValueAndValidity();
      }
    });

    (workflowStepCompletionFormGroup.controls['auto_emails'] as FormArray).controls.forEach(a => {
      let autoEmailFormGroup = (a as FormGroup);
      let files: WorkflowFile[] = autoEmailFormGroup.controls['files'].value as WorkflowFile[] || [];
      
      let fileIndex = files.findIndex(i => i.name == file.name);
      if (fileIndex !== -1) {
        files.splice(fileIndex, 1);
        autoEmailFormGroup.controls['files'].updateValueAndValidity();
      }
    });

    if (workflowStepCompletionFormGroup.controls['next_step'] && workflowStepCompletionFormGroup.controls['next_step'].value) {
      let stepCompletionsFormArray = ((workflowStepCompletionFormGroup.controls['next_step'] as FormGroup).controls['step_completions'] as FormArray);
      
      for (let i = 0; i < stepCompletionsFormArray.length; i++) {
        this.removeFileFromRules(file, stepCompletionsFormArray.at(i) as FormGroup);
      }
    }
  }

  renameFileInRules(oldFileName: string, newFileName: string, workflowStepCompletionFormGroup: FormGroup = (this.workflowTemplateFormGroup.controls['initial_step_completion'] as FormGroup)) {
    (workflowStepCompletionFormGroup.controls['auto_uploads'] as FormArray).controls.forEach(a => {
      let autoUploadFormGroup = (a as FormGroup);
      let files: WorkflowFile[] = autoUploadFormGroup.controls['files'].value as WorkflowFile[] || [];
      
      let fileIndex = files.findIndex(i => i.name == oldFileName);
      if (fileIndex !== -1) {
        files[fileIndex].name = newFileName;
        autoUploadFormGroup.controls['files'].updateValueAndValidity();
      }
    });

    (workflowStepCompletionFormGroup.controls['auto_emails'] as FormArray).controls.forEach(a => {
      let autoEmailFormGroup = (a as FormGroup);
      let files: WorkflowFile[] = autoEmailFormGroup.controls['files'].value as WorkflowFile[] || [];
      
      let fileIndex = files.findIndex(i => i.name == oldFileName);
      if (fileIndex !== -1) {
        files[fileIndex].name = newFileName;
        autoEmailFormGroup.controls['files'].updateValueAndValidity();
      }
    });

    if (workflowStepCompletionFormGroup.controls['next_step'] && workflowStepCompletionFormGroup.controls['next_step'].value) {
      let stepCompletionsFormArray = ((workflowStepCompletionFormGroup.controls['next_step'] as FormGroup).controls['step_completions'] as FormArray);
      
      for (let i = 0; i < stepCompletionsFormArray.length; i++) {
        this.renameFileInRules(oldFileName, newFileName, stepCompletionsFormArray.at(i) as FormGroup);
      }
    }
  }

  removeFormFromRules(form: WorkflowForm, workflowStepCompletionFormGroup: FormGroup = (this.workflowTemplateFormGroup.controls['initial_step_completion'] as FormGroup)){
    (workflowStepCompletionFormGroup.controls['auto_uploads'] as FormArray).controls.forEach(a => {
      let autoUploadFormGroup = (a as FormGroup);
      let forms: WorkflowForm[] = autoUploadFormGroup.controls['forms'].value as WorkflowForm[] || [];
      
      let formIndex = forms.findIndex(i => i.name == form.name);
      if (formIndex !== -1) {
        forms.splice(formIndex, 1);
        autoUploadFormGroup.controls['forms'].updateValueAndValidity();
      }
    });

    (workflowStepCompletionFormGroup.controls['auto_emails'] as FormArray).controls.forEach(a => {
      let autoEmailFormGroup = (a as FormGroup);
      let forms: WorkflowForm[] = autoEmailFormGroup.controls['forms'].value as WorkflowForm[] || [];
      
      let formIndex = forms.findIndex(i => i.name == form.name);
      if (formIndex !== -1) {
        forms.splice(formIndex, 1);
        autoEmailFormGroup.controls['forms'].updateValueAndValidity();
      }
    });

    if (workflowStepCompletionFormGroup.controls['next_step'] && workflowStepCompletionFormGroup.controls['next_step'].value) {
      let stepCompletionsFormArray = ((workflowStepCompletionFormGroup.controls['next_step'] as FormGroup).controls['step_completions'] as FormArray);
      
      for (let i = 0; i < stepCompletionsFormArray.length; i++) {
        this.removeFormFromRules(form, stepCompletionsFormArray.at(i) as FormGroup);
      }
    }
  }

  renameFormInRules(oldFormName: string, newFormName: string, workflowStepCompletionFormGroup: FormGroup = (this.workflowTemplateFormGroup.controls['initial_step_completion'] as FormGroup)) {
    
    this.renameFormInWorkflowExecVariable(this.workflowTemplateFormGroup.value.observers_users_variable, oldFormName, newFormName);
    this.renameFormInWorkflowExecVariable(this.workflowTemplateFormGroup.value.observers_teams_variable, oldFormName, newFormName);

    this.renameFormInRules_recursive(oldFormName, newFormName, workflowStepCompletionFormGroup);
  }

  renameFormInRules_recursive(oldFormName: string, newFormName: string, workflowStepCompletionFormGroup: FormGroup = (this.workflowTemplateFormGroup.controls['initial_step_completion'] as FormGroup)) {
    let formRenameRegex = new RegExp(`(.*{{)${oldFormName}(>>(([^}][^}]?|[^}]}?)*)}}.*)`);
    let formNameReplacement = `$1${newFormName}$2`;
    (workflowStepCompletionFormGroup.controls['auto_uploads'] as FormArray).controls.forEach(a => {
      let autoUploadFormGroup = (a as FormGroup);
      let autoUpload = <WorkflowAutoUpload>autoUploadFormGroup.value;
      let forms: WorkflowForm[] = autoUploadFormGroup.controls['forms'].value as WorkflowForm[] || [];
      
      let formIndex = forms.findIndex(i => i.name == oldFormName);
      if (formIndex !== -1) {
        forms[formIndex].name = newFormName;
        autoUploadFormGroup.controls['forms'].updateValueAndValidity();
      }

      this.renameFormInWorkflowExecVariable(autoUploadFormGroup.value.path_variable, oldFormName, newFormName);
      autoUploadFormGroup.value.file_rename = autoUpload.file_rename && autoUpload.file_rename.replace(formRenameRegex, formNameReplacement);
      autoUploadFormGroup.value.file_name_prefix = autoUpload.file_name_prefix && autoUpload.file_name_prefix.replace(formRenameRegex, formNameReplacement);
      autoUploadFormGroup.value.file_name_suffix = autoUpload.file_name_suffix && autoUpload.file_name_suffix.replace(formRenameRegex, formNameReplacement);
      autoUploadFormGroup.value.additional_paths = autoUpload.additional_paths && autoUpload.additional_paths.flatMap((p) => p.replace(formRenameRegex, formNameReplacement));
      (autoUpload.metadata || []).forEach((m, index) => {
        if (m.auto_upload_metadata_settings && m.auto_upload_metadata_settings.value) {
          autoUploadFormGroup.get('metadata.' + index + '.auto_upload_metadata_settings.value').setValue(m.auto_upload_metadata_settings.value.replace(formRenameRegex, formNameReplacement));
        }
      });
    });

    (workflowStepCompletionFormGroup.controls['auto_emails'] as FormArray).controls.forEach(a => {
      let autoEmailFormGroup = (a as FormGroup);
      let forms: WorkflowForm[] = autoEmailFormGroup.controls['forms'].value as WorkflowForm[] || [];
      
      let formIndex = forms.findIndex(i => i.name == oldFormName);
      if (formIndex !== -1) {
        forms[formIndex].name = newFormName;
        autoEmailFormGroup.controls['forms'].updateValueAndValidity();
      }

      this.renameFormInWorkflowExecVariable(autoEmailFormGroup.value.from_variable, oldFormName, newFormName);
      this.renameFormInWorkflowExecVariable(autoEmailFormGroup.value.to_users_variable, oldFormName, newFormName);
      this.renameFormInWorkflowExecVariable(autoEmailFormGroup.value.to_teams_variable, oldFormName, newFormName);
      this.renameFormInWorkflowExecVariable(autoEmailFormGroup.value.to_contacts_variable, oldFormName, newFormName);
      this.renameFormInWorkflowExecVariable(autoEmailFormGroup.value.cc_users_variable, oldFormName, newFormName);
      this.renameFormInWorkflowExecVariable(autoEmailFormGroup.value.cc_teams_variable, oldFormName, newFormName);
      this.renameFormInWorkflowExecVariable(autoEmailFormGroup.value.cc_contacts_variable, oldFormName, newFormName);
      this.renameFormInWorkflowExecVariable(autoEmailFormGroup.value.bcc_users_variable, oldFormName, newFormName);
      this.renameFormInWorkflowExecVariable(autoEmailFormGroup.value.bcc_teams_variable, oldFormName, newFormName);
      this.renameFormInWorkflowExecVariable(autoEmailFormGroup.value.bcc_contacts_variable, oldFormName, newFormName);
      
    });

    (workflowStepCompletionFormGroup.controls['auto_starts'] as FormArray).controls.forEach(a => {
      let autoStartFormGroup = (a as FormGroup);
      let autoStartFormSettings: AutoStartFormSettings[] = autoStartFormGroup.controls['forms'].value as AutoStartFormSettings[] || [];
      
      autoStartFormSettings.forEach((f, index) => {
        if (f.initiator_workflow_form_name == oldFormName) {
          autoStartFormGroup.get('forms.' + index + '.initiator_workflow_form_name').setValue(newFormName);
        }

        (f.submission_value || []).forEach((s, submissionIndex) => {
          this.renameFormInWorkflowExecVariable(autoStartFormGroup.get('forms.' + index + '.submission_value.' + submissionIndex + '.exec_variable').value, oldFormName, newFormName);
        });
      });      
    });

    if (workflowStepCompletionFormGroup.controls['next_step'] && workflowStepCompletionFormGroup.controls['next_step'].value) {
      let nextStepFormGroup = (workflowStepCompletionFormGroup.controls['next_step'] as FormGroup);

      this.renameFormInWorkflowExecVariable(nextStepFormGroup.value.target_users_variable, oldFormName, newFormName);
      this.renameFormInWorkflowExecVariable(nextStepFormGroup.value.target_teams_variable, oldFormName, newFormName);
      this.renameFormInWorkflowExecVariable(nextStepFormGroup.value.target_contacts_variable, oldFormName, newFormName);

      let stepCompletionsFormArray = (nextStepFormGroup.controls['step_completions'] as FormArray);
      
      for (let i = 0; i < stepCompletionsFormArray.length; i++) {
        var stepCompletion: WorkflowStepCompletion = (stepCompletionsFormArray.at(i) as FormGroup).value;
        (stepCompletion.forms || []).forEach((f, index) => {
          if (f.name == oldFormName) {
            (stepCompletionsFormArray.at(i) as FormGroup).get('forms.' + index + '.name').setValue(newFormName);
          }
        });
        this.renameFormInRules_recursive(oldFormName, newFormName, stepCompletionsFormArray.at(i) as FormGroup);
      }
    }  
  }

  renameFormInWorkflowExecVariable(workflowExecVar: WorkflowExecVariable, oldFormName: string, newFormName: string) {
    if (workflowExecVar && workflowExecVar.workflow_form_name == oldFormName) {
      workflowExecVar.workflow_form_name = newFormName;
    }
  }

  isFormUsedInWorkflow(formName: string, workflowStepCompletionFormGroup: FormGroup = (this.workflowTemplateFormGroup.controls['initial_step_completion'] as FormGroup)) : string[] | null {
    
    let result: string[] = [];

    if (this.isFormUsedInWorkflowExecVariable(this.workflowTemplateFormGroup.value.observers_users_variable, formName) ||
        this.isFormUsedInWorkflowExecVariable(this.workflowTemplateFormGroup.value.observers_teams_variable, formName)) {
        result.push('Workflow Supervisor(s)');
    }

    result = result.concat(this.isFormUsedInWorkflow_recursive(formName, workflowStepCompletionFormGroup));

    return result.length > 0? result: null;
  }
  
  isFormUsedInWorkflow_recursive(formName: string, workflowStepCompletionFormGroup: FormGroup = (this.workflowTemplateFormGroup.controls['initial_step_completion'] as FormGroup)) {
    let result: string[] = [];
    (workflowStepCompletionFormGroup.controls['auto_uploads'] as FormArray).controls.forEach(a => {
      let autoUploadFormGroup = (a as FormGroup);

      let autoUpload = <WorkflowAutoUpload>autoUploadFormGroup.value;
      (autoUpload.forms || []).forEach((f) => {
        if (f.name == formName) {
          result.push('Decision: ' + workflowStepCompletionFormGroup.value.name + ' - ' + 'auto save rule.')
        }
      });

      let formNameRegex = new RegExp(`.*{{${formName}>>(([^}][^}]?|[^}]}?)*)}}.*`);
      if (formNameRegex.test(autoUpload.file_rename) || 
          formNameRegex.test(autoUpload.file_name_prefix) || 
          formNameRegex.test(autoUpload.file_name_suffix) ||
          (autoUpload.additional_paths || []).find((v) => formNameRegex.test(v)) ||
          (autoUpload.metadata || []).find((m) => { return m.auto_upload_metadata_settings && formNameRegex.test(m.auto_upload_metadata_settings.value);}) ||
          this.isFormUsedInWorkflowExecVariable(autoUpload.path_variable, formName)) {
        result.push('Decision: ' + workflowStepCompletionFormGroup.value.name + ' - ' + 'auto save rule.')
      }
    });

    (workflowStepCompletionFormGroup.controls['auto_emails'] as FormArray).controls.forEach(a => {
      let autoEmailFormGroup = (a as FormGroup);

      if (this.isFormUsedInWorkflowExecVariable(autoEmailFormGroup.value.from_variable, formName) ||
        this.isFormUsedInWorkflowExecVariable(autoEmailFormGroup.value.to_users_variable, formName) ||
        this.isFormUsedInWorkflowExecVariable(autoEmailFormGroup.value.to_teams_variable, formName) ||
        this.isFormUsedInWorkflowExecVariable(autoEmailFormGroup.value.to_contacts_variable, formName) ||
        this.isFormUsedInWorkflowExecVariable(autoEmailFormGroup.value.cc_users_variable, formName) ||
        this.isFormUsedInWorkflowExecVariable(autoEmailFormGroup.value.cc_teams_variable, formName) ||
        this.isFormUsedInWorkflowExecVariable(autoEmailFormGroup.value.cc_contacts_variable, formName) ||
        this.isFormUsedInWorkflowExecVariable(autoEmailFormGroup.value.bcc_users_variable, formName) ||
        this.isFormUsedInWorkflowExecVariable(autoEmailFormGroup.value.bcc_teams_variable, formName) ||
        this.isFormUsedInWorkflowExecVariable(autoEmailFormGroup.value.bcc_contacts_variable, formName)) {
        result.push(workflowStepCompletionFormGroup.value.name + ' - ' + 'auto email rule.')
      }
      
    });

    (workflowStepCompletionFormGroup.controls['auto_starts'] as FormArray).controls.forEach(a => {
      let autoStart = <WorkflowAutoStart>((a as FormGroup).value);

      (autoStart.forms || []).forEach((autoStartFormSettings) => {
        if (autoStartFormSettings.initiator_workflow_form_name == formName) {
          result.push('Decision: ' + workflowStepCompletionFormGroup.value.name + ' - ' + 'auto start workflow: ' + autoStart.workflow_template.name);
        }

        (autoStartFormSettings.submission_value || []).forEach((s) => {
          if (this.isFormUsedInWorkflowExecVariable(s.exec_variable, formName)) {
            result.push('Decision: ' + workflowStepCompletionFormGroup.value.name + ' - ' + 'auto start workflow: ' + autoStart.workflow_template.name);
          }
        });
      });
    });

    if (workflowStepCompletionFormGroup.controls['next_step'] && workflowStepCompletionFormGroup.controls['next_step'].value) {
      let nextStepFormGroup = (workflowStepCompletionFormGroup.controls['next_step'] as FormGroup);

      if (this.isFormUsedInWorkflowExecVariable(nextStepFormGroup.value.target_users_variable, formName) ||
        this.isFormUsedInWorkflowExecVariable(nextStepFormGroup.value.target_teams_variable, formName) ||
        this.isFormUsedInWorkflowExecVariable(nextStepFormGroup.value.target_contacts_variable, formName)) {
          result.push('Step: ' + nextStepFormGroup.value.name + ' - ' + 'assignee(s).')
        }

      let stepCompletionsFormArray = (nextStepFormGroup.controls['step_completions'] as FormArray);
      
      for (let i = 0; i < stepCompletionsFormArray.length; i++) {
        var stepCompletion: WorkflowStepCompletion = (stepCompletionsFormArray.at(i) as FormGroup).value;
        (stepCompletion.forms || []).forEach((f) => {
          if (f.name == formName) {
            result.push('Step: ' + nextStepFormGroup.value.name + ' - ' + ' decision: ' + stepCompletion.name + ' required forms.');
          }
        });
        result = result.concat(this.isFormUsedInWorkflow_recursive(formName, stepCompletionsFormArray.at(i) as FormGroup));
      }
    }
    
    return result;
  }

  isFormUsedInWorkflowExecVariable(workflowExecVar: WorkflowExecVariable, formName: string) : boolean {
    return workflowExecVar && workflowExecVar.workflow_form_name == formName;
  }

  findStepById(stepId: number, stepFormGroup = null) {
    
    if (this.workflowTemplateFormGroup) {
      if (!stepFormGroup) {
        stepFormGroup = this.workflowTemplateFormGroup.get('initial_step_completion.next_step');
        if (!stepFormGroup) {
          return null;
        }
      }
      if (stepFormGroup.value.id == stepId) {
        return stepFormGroup;
      } else {
        let stepCompletionsFormArray = (stepFormGroup.controls['step_completions'] as FormArray) || new FormArray([]);
    
        for (let i = 0; i < stepCompletionsFormArray.length; i++) {
          var stepCompletion: WorkflowStepCompletion = (stepCompletionsFormArray.at(i) as FormGroup).value;
          if (stepCompletion.next_step) {
            var stepToFind = this.findStepById(stepId, ((stepCompletionsFormArray.at(i) as FormGroup).controls['next_step'] as FormGroup));
            if (stepToFind) {
              return stepToFind;
            }
          }
        }
      }
    }

    return null;
  }

  saveWorkflowTemplate() {
      this.isLoading = true;
      this.updateAllValueAndValidity();

      return this.workflowTemplateService.put(this.workflowTemplateFormGroup.value as WorkflowTemplate).pipe(finalize(() => {
        this.isLoading = false;
      })).pipe(tap(((result: WorkflowTemplate) => {
        this.init(result);
      })));
  }

  publishWorkflowTemplate() {
    this.isLoading = true;

    return this.workflowTemplateService.publish(this.workflowTemplateFormGroup.value.id).pipe(finalize(() => {
      this.isLoading = false;
    }));
  }
  
  convertWorkflowFullTreeToPrimeTree() {
    this.updateAllValueAndValidity();

    let primeTree: TreeNode[] = [
      {
        expanded: true,
        data: { initial_step_completion: this.workflowTemplateFormGroup.controls.initial_step_completion,
                type: 'init'
              },
        label: 'Start',
        type: 'initial_step_completion',
        styleClass: 'p-start',
        children: []
      }
    ]
    if(this.workflowTemplateFormGroup.value.initial_step_completion.next_step &&
      this.workflowTemplateFormGroup.value.initial_step_completion.next_step.name)
      this.convertStepsIntoTree((this.workflowTemplateFormGroup.controls['initial_step_completion'] as FormGroup).controls['next_step'] as FormGroup, primeTree[0])
    return primeTree;
  }

  convertStepsIntoTree(step: FormGroup, root: TreeNode) {

    if (step && step.value.name) {
      // Add the step
      root.children.push({
        expanded: true,
        label: step.value.name,
        data: { step: step },
        type: 'step',
        styleClass: 'p-step',
        children: []
      });

      // Add the step completions as children to the step (root.children[0])
      if(step.controls['step_completions']) {
        let i = 0;
        (step.controls['step_completions'] as FormArray).controls.forEach( (s: FormGroup) => {
          root.children[0].children.push({
            expanded: true,
            data: { 
                    step_completion: s 
                  },
            label: s.value.name,
            type: 'step_completion',
            styleClass: 'p-step-completion',
            children: []
          });
          if (s.value.next_step) {
            this.convertStepsIntoTree(s.controls['next_step'] as FormGroup, root.children[0].children[i])
          }
          i++;
        });
      }
    }
  }

  updateAllValueAndValidity() {
    let firstStepFormGroup: FormGroup = (this.workflowTemplateFormGroup.controls['initial_step_completion'] as FormGroup).get('next_step') as FormGroup;
    if (firstStepFormGroup) {
      this.updateAllValueAndValidityRecursive(firstStepFormGroup);
    }
    this.workflowTemplateFormGroup.controls['initial_step_completion'].updateValueAndValidity();
  }

  private updateAllValueAndValidityRecursive(step: FormGroup) {
    if (step) {
      step.updateValueAndValidity();
      if (step.get('step_completions')) {
        (step.controls['step_completions'] as FormArray).controls.forEach((sc: FormGroup) => {
          sc.updateValueAndValidity();
          if(sc.get('next_step') && sc.controls['next_step'].value) {
            this.updateAllValueAndValidityRecursive(sc.controls['next_step'] as FormGroup)
          }
        });
      }
    }
  }
}
